import React from "react";

import { GradientDefs } from "../../GradientDefs";

/**
 * @component TextStyleStrikeIcon
 * @description
 *
 * The React Icon component is a visual element that displays an icon to represent a concept, object, or action.
 * The Icon component is
 * customizable, allowing for variations in size, color, and style to fit the needs of the application.
 *
 * @prop {string} [gradient] - Optional CSS linear-gradient string to apply a custom fill.
 * Format: "linear-gradient(<angle>deg, <color1>, <color2>, ...)"
 *
 * @example
 * return (
 * <TextStyleStrikeIcon gradient="linear-gradient(135deg, #4da2ed, #f72222)" />
 * )
 *
 * @example
 * return (
 *   <TextStyleStrikeIcon className="mr-2" />
 * )
 *
 * @see
 * https://system.activecollab.com/?path=/story/foundation-icons-icons--icons
 * @see
 * https://design.activecollab.com/docs/foundations/icons
 */
const TextStyleStrikeIcon = React.forwardRef(
  (
    {
      gradient,
      ...props
    }: React.SVGProps<SVGSVGElement> & {
      gradient?: string;
    },
    ref?: React.Ref<SVGSVGElement>
  ) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={24}
      height={24}
      viewBox="0 0 24 24"
      data-testid="TextStyleStrikeIcon"
      fill={
        gradient
          ? "url(#icon-gradient)"
          : props?.fill
          ? props.fill
          : "var(--color-theme-600)"
      }
      focusable={false}
      ref={ref}
      {...props}
    >
      <GradientDefs gradient={gradient} />
      <path
        fillRule="evenodd"
        d="M19.19 15.51Q19.19 16.63 18.78 17.55Q18.37 18.47 17.51 19.13Q16.65 19.78 15.31 20.14Q13.96 20.5 12.11 20.5Q10.47 20.5 9.22 20.21Q7.96 19.91 7.06 19.34Q6.17 18.76 5.61 17.91Q5.05 17.06 4.81 15.96L8.15 15.41Q8.29 15.96 8.56 16.42Q8.83 16.88 9.3 17.21Q9.78 17.55 10.49 17.73Q11.2 17.91 12.21 17.91Q13.96 17.91 14.9 17.38Q15.83 16.84 15.83 15.7Q15.83 15.02 15.45 14.59Q15.08 14.16 14.46 13.88Q13.83 13.59 13.03 13.41Q12.22 13.22 11.34 13.04Q10.63 12.88 9.93 12.7Q9.23 12.53 8.59 12.28Q7.95 12.04 7.4 11.7Q6.85 11.36 6.44 10.86Q6.04 10.36 5.81 9.69Q5.58 9.02 5.58 8.12Q5.58 6.89 6.07 6.01Q6.57 5.13 7.44 4.57Q8.31 4.02 9.52 3.76Q10.73 3.5 12.16 3.5Q13.78 3.5 14.93 3.75Q16.09 4 16.87 4.52Q17.65 5.04 18.1 5.82Q18.55 6.6 18.76 7.64L15.41 8.1Q15.15 7.03 14.36 6.49Q13.57 5.95 12.09 5.95Q11.17 5.95 10.57 6.11Q9.97 6.27 9.61 6.54Q9.25 6.81 9.1 7.16Q8.95 7.52 8.95 7.92Q8.95 8.53 9.24 8.92Q9.53 9.32 10.06 9.58Q10.59 9.84 11.32 10.02Q12.05 10.21 12.93 10.39Q13.71 10.56 14.47 10.74Q15.23 10.92 15.92 11.17Q16.61 11.41 17.21 11.77Q17.81 12.12 18.25 12.63Q18.69 13.14 18.94 13.84Q19.19 14.54 19.19 15.51Z"
        clipRule="evenodd"
      />
      <rect x="3" y="11" width="18" height="2" rx="1" />
    </svg>
  )
);
TextStyleStrikeIcon.displayName = "TextStyleStrikeIcon";
export default TextStyleStrikeIcon;
